"use client";

import { useEffect, useRef, useState } from "react";
import { WalletReadyState } from "@solana/wallet-adapter-base";

import {
  hasSignedOut,
  reconnectIfTrusted,
  rememberReturning,
} from "@/lib/wallet/phantom-trusted";

/**
 * How long a returning holder's page waits for a reattach before it shows the
 * disconnected screen. Phantom reattaches about 1.3s after navigation; a
 * wallet that never answers must not hold the page for longer than this.
 */
export const REATTACH_WAIT_MS = 2_500;

interface ReattachInput {
  adapter: {
    autoConnect: () => Promise<void>;
    connect: () => Promise<void>;
  };
  address: string | null;
  isPhantom: boolean;
  readyState: WalletReadyState;
  /** The server-read returning-holder cookie, so hydration agrees with it. */
  returning: boolean;
}

/**
 * Silent re-attach, awaited rather than sampled: Phantom reattaches a trusted
 * site a second or so after load, and `onlyIfTrusted` is the call that waits
 * for that without ever raising an approval. Guarded three ways: the
 * extension must be installed, this must not have run before, and the holder
 * must not have logged out in this tab.
 *
 * `Installed` rather than merely "a wallet is injected", because
 * `adapter.connect()` throws `WalletNotReadyError` below that and this has
 * only one attempt in it. The adapter polls for the extension and emits
 * `readyStateChange`, which re-runs this, so an extension that registers late
 * is waited for rather than missed.
 *
 * No cleanup on the attempt, deliberately. Cancelling it on unmount would mean
 * React's StrictMode double-mount cancels the first run while the ref makes
 * the second return early, so the re-attach would never happen in development
 * — which is where it gets looked at.
 *
 * Answers whether a reattach may still land: true from the first render for a
 * returning holder until the attempt settles, a wallet attaches, or
 * `REATTACH_WAIT_MS` passes. Always false for a first-time visitor.
 */
export function usePhantomReattach({
  adapter,
  address,
  isPhantom,
  readyState,
  returning,
}: ReattachInput): boolean {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (address) rememberReturning();
  }, [address]);

  useEffect(() => {
    if (!returning) return;
    // Zero for a holder who logged out here: nothing is going to reattach.
    const timer = setTimeout(
      () => setSettled(true),
      hasSignedOut() ? 0 : REATTACH_WAIT_MS
    );
    return () => clearTimeout(timer);
  }, [returning]);

  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current || address) return;
    if (hasSignedOut() || readyState !== WalletReadyState.Installed) return;
    attempted.current = true;
    void (async () => {
      /* A standard wallet reattaches with a silent `standard:connect`, which
         likewise shows nothing to a holder who has not approved this site. */
      if (!isPhantom) await adapter.autoConnect().catch(() => undefined);
      /* Untrusted answers false, having shown the holder nothing. Trusted,
         the wallet is connected, so this only reads the key and emits. */
      else if (await reconnectIfTrusted())
        await adapter.connect().catch(() => undefined);
      setSettled(true);
    })();
  }, [adapter, address, isPhantom, readyState]);

  return returning && !address && !settled;
}
